import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AMCActions } from 'src/Redux';
import { FormField, FormFieldType } from '../FormField';
import { AlertNotification, Typography } from '../../shared';
import { checkMax, checkMin, composeValidatorsJSON, i18, isZeroDecimalCurrency, numberFormat, required, trb } from '../../Utilities';

type AmountFieldName = 'AmountBuy' | 'AmountSell';

interface ILastCalculatedAmounts {
  AmountBuy: number | null;
  AmountSell: number | null;
  FromAccount: string | null;
  Currency: string | null;
}

interface IPendingRateRequest {
  fieldName: AmountFieldName;
  amount: number;
  FromAccount: string | null;
  Currency: string | null;
}

export interface IForexRate {
  AmountFrom: number | null;
  AmountTo: number | null;
  BankRate: number | null;
}

export interface IPaymentValues {
  FromAccount: string | null;
  BeneficiaryAccountNumber: string | null;
  Currency: string | null;
  AmountBuy: number | null;
  AmountSell: number | null;
  FixRate: number | null;
  FromCurrency?: string | null;
  canBuy: boolean;
}

export interface IFormApi {
  change: (field: string, value: any) => void;
  blur: (field: string) => void;
}

export interface IForexTimerValues {
  setTotalTime?: (value: number | null) => void;
}

export interface IProps {
  values: {
    Payment: IPaymentValues;
  };
  form: IFormApi;
  forexTimerValues: IForexTimerValues;
}

export const FieldsForexExchangeAmounts = ({ values, form, forexTimerValues }: IProps) => {
  const dispatch = useDispatch();

  const { forexRateLoader, forexRate, forexCurrency } = useSelector((state: any) => state.AMCReducers);
  const { forexPayments } = useSelector((state: any) => state.PMCReducers);

  const [forexRates, setForexRates] = useState<IForexRate | null>(null);
  const [resetCounter, setResetCounter] = useState(0);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const lastCalculatedAmountsRef = useRef<ILastCalculatedAmounts>({
    AmountBuy: null,
    AmountSell: null,
    FromAccount: null,
    Currency: null,
  });

  const pendingRateRequestRef = useRef<IPendingRateRequest | null>(null);
  const isSellZeroDecimal = isZeroDecimalCurrency(values.Payment.FromCurrency);
  const isBuyZeroDecimal = isZeroDecimalCurrency(values.Payment.Currency);
  const isSellZeroDecimalRef = useRef(isSellZeroDecimal);
  const isBuyZeroDecimalRef = useRef(isBuyZeroDecimal);

  isSellZeroDecimalRef.current = isSellZeroDecimal;
  isBuyZeroDecimalRef.current = isBuyZeroDecimal;

  const currencyFormatConfig = numberFormat().currency;
  const zeroDecimalFormat = {
    ...currencyFormatConfig,
    decimalScale: 0,
    fixedDecimalScale: true,
    suffix: `${currencyFormatConfig.decimalSeparator}00`,
  };

  const debounceTO = 4000;

  const clearDebounce = () => {
    if (debounceRef.current !== null) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  };

  const getAccountCurrency = (accountId: string) => {
    return forexPayments?.InitialAccounts.find((account: any) => account.AccountID === accountId)?.CurrencyName;
  };

  const handleChangeRateSearch = async (amount: number | null, fieldName: AmountFieldName) => {
    if (amount === null) {
      return;
    }

    const numericAmount = Number(amount);

    if (!Number.isFinite(numericAmount)) {
      return;
    }

    const { FromAccount, Currency } = values.Payment;
    const lastCalculated = lastCalculatedAmountsRef.current;
    const pendingRequest = pendingRateRequestRef.current;

    const isSameCalculatedValue = lastCalculated.FromAccount === FromAccount && lastCalculated.Currency === Currency && lastCalculated[fieldName] === numericAmount;

    const isSamePendingRequest =
      pendingRequest?.fieldName === fieldName && pendingRequest.amount === numericAmount && pendingRequest.FromAccount === FromAccount && pendingRequest.Currency === Currency;

    if (isSameCalculatedValue || isSamePendingRequest) {
      return;
    }

    const oppositeField = fieldName === 'AmountBuy' ? 'Payment.AmountSell' : 'Payment.AmountBuy';

    form.change(oppositeField, null);

    if (fieldName === 'AmountBuy') {
      const { minAmount, maxAmount } = forexCurrency?.find(({ currency }: any) => currency === Currency) ?? {};

      if ((minAmount != null && numericAmount < minAmount) || (maxAmount != null && numericAmount > maxAmount)) {
        return;
      }
    }

    pendingRateRequestRef.current = {
      fieldName,
      amount: numericAmount,
      FromAccount,
      Currency,
    };

    try {
      if (fieldName === 'AmountBuy') {
        await dispatch<any>(
          AMCActions.getForexRate({
            from: getAccountCurrency(String(FromAccount)),
            to: Currency,
            amountFrom: null,
            amountTo: numericAmount,
          })
        );

        return;
      }

      await dispatch<any>(
        AMCActions.getForexRate({
          from: getAccountCurrency(String(FromAccount)),
          to: Currency,
          amountFrom: numericAmount,
          amountTo: null,
        })
      );
    } finally {
      pendingRateRequestRef.current = null;
    }
  };

  useEffect(() => {
    if (forexRate) {
      setForexRates(forexRate);
    }
  }, [forexRate]);

  useEffect(() => {
    if (!forexRates) {
      return;
    }

    const amountSell = isSellZeroDecimalRef.current && forexRates.AmountFrom != null ? Math.round(forexRates.AmountFrom) : forexRates.AmountFrom;
    const amountBuy = isBuyZeroDecimalRef.current && forexRates.AmountTo != null ? Math.round(forexRates.AmountTo) : forexRates.AmountTo;

    lastCalculatedAmountsRef.current = {
      AmountSell: amountSell,
      AmountBuy: amountBuy,
      FromAccount: values.Payment.FromAccount,
      Currency: values.Payment.Currency,
    };

    form.change('Payment.AmountSell', amountSell);
    form.change('Payment.AmountBuy', amountBuy);
    form.change('Payment.FixRate', forexRates.BankRate);

    form.blur('Payment.AmountSell');
    form.blur('Payment.AmountBuy');

    setResetCounter((previousValue) => previousValue + 1);
  }, [forexRates]);

  useEffect(() => {
    setForexRates(null);
  }, [resetCounter]);

  useEffect(() => {
    if (values.Payment.AmountBuy && !forexRates) {
      clearDebounce();

      debounceRef.current = setTimeout(() => {
        void handleChangeRateSearch(values.Payment.AmountBuy, 'AmountBuy');
      }, debounceTO);
    }

    return clearDebounce;
  }, [values.Payment.AmountBuy]);

  useEffect(() => {
    if (values.Payment.AmountSell && !forexRates) {
      clearDebounce();

      debounceRef.current = setTimeout(() => {
        void handleChangeRateSearch(values.Payment.AmountSell, 'AmountSell');
      }, debounceTO);
    }

    return clearDebounce;
  }, [values.Payment.AmountSell]);

  const handleBlurAmount = (value: number, fieldName: AmountFieldName) => {
    clearDebounce();
    void handleChangeRateSearch(value, fieldName);
  };

  useEffect(() => {
    if (values.Payment.FromAccount) {
      const accountCurrency = getAccountCurrency(values.Payment.FromAccount);

      if (accountCurrency) {
        form.change('Payment.FromCurrency', accountCurrency);
      }

      forexTimerValues.setTotalTime?.(null);
    }
  }, [values.Payment.FromAccount]);

  useEffect(() => {
    if (values.Payment.Currency) {
      form.change('Payment.AmountBuy', null);
      form.change('Payment.AmountSell', null);
      form.change('Payment.FixRate', null);

      forexTimerValues.setTotalTime?.(null);
    }
  }, [values.Payment.Currency]);

  const checkMinMaxValues = (currencies: any) => (value: any, _values: any, formValues: any) => {
    const { minAmount, maxAmount } = currencies?.find(({ currency }: any) => currency === formValues?.Payment?.Currency) ?? {};

    if (minAmount != null) {
      const minError = checkMin(minAmount)(value);

      if (minError) {
        return minError;
      }
    }

    if (maxAmount != null) {
      const maxError = checkMax(maxAmount)(value);

      if (maxError) {
        return maxError;
      }
    }

    return null;
  };

  if (!values.Payment.canBuy) {
    return null;
  }

  return (
    <div className="col-12">
      {(isSellZeroDecimal || isBuyZeroDecimal) && (
        <AlertNotification
          type="warning"
          message={trb(i18.Messages.ForexExchangeJpyRoundingWarning)}
          alertClassName="py-3 mb-3"
        />
      )}

      <Typography.Paragraph
        type="secondary"
        className="pl-3 mb-2"
      >
        {trb(i18.Labels.CurrencyFrom)}
      </Typography.Paragraph>

      <FormField
        title={i18.Labels.Amount}
        name="Payment.AmountSell"
        component={FormFieldType.number}
        loading={forexRateLoader}
        disabled={forexRateLoader || (isBuyZeroDecimal && !isSellZeroDecimal)}
        formatNumber={isSellZeroDecimal ? zeroDecimalFormat : undefined}
        validate={composeValidatorsJSON([required, checkMinMaxValues(forexCurrency)], form, values)}
        handleBlur={(value) => {
          handleBlurAmount(value, 'AmountSell');
        }}
      />

      <Typography.Paragraph
        type="secondary"
        className="pl-3 mb-2"
      >
        {trb(i18.Labels.CurrencyTo)}
      </Typography.Paragraph>

      <FormField
        title={i18.Labels.Amount}
        name="Payment.AmountBuy"
        component={FormFieldType.number}
        loading={forexRateLoader}
        disabled={forexRateLoader || (isSellZeroDecimal && !isBuyZeroDecimal)}
        formatNumber={isBuyZeroDecimal ? zeroDecimalFormat : undefined}
        validate={composeValidatorsJSON([required, checkMinMaxValues(forexCurrency)], form, values)}
        handleBlur={(value) => {
          handleBlurAmount(value, 'AmountBuy');
        }}
      />
    </div>
  );
};
